import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentitySeasonGroup, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service for seasonGroup data.
 */
@Injectable()
export class IdentitySeasonGroupService {
    public static INCLUDE_TEAMS: string = 'teams';
    public static INCLUDE_SEASON_W_ORGANIZATION: string = 'season.organization';

    private readonly identityApiBaseUrl: string;
    private cachedSeasonGroupsMap: { [apiRequest: string]: PagedResponse } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = this.http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all seasonGroups for the specified event
     */
    public getIndexListResponse(
        seasonId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        const apiRequestUrl: string = this.getApiUrl(seasonId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(apiRequestUrl);
    }

    /**
     * Get seasonGroups by a url. This is used for paging through seasonGroups since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(url: string, forceRefresh: boolean = false): Observable<PagedResponse> {
        if (!forceRefresh &&
            this.cachedSeasonGroupsMap[url]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedSeasonGroupsMap[url].clone());
        }
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const included: any[] = response.included;
                const json: any = response;

                const seasonGroups: IdentitySeasonGroup[] = [];
                for (const seasonGroup of json.data) {
                    seasonGroups.push((new IdentitySeasonGroup()).deserialize(seasonGroup, included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    seasonGroups,
                );
            }));
    }

    /**
     * Get a single seasonGroup's data
     */
    public get(seasonId: string, seasonGroupId: string, include: string[] = null): Observable<IdentitySeasonGroup> {
        const apiRequestUrl: string = this.getApiUrl(seasonId, seasonGroupId, include);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => new IdentitySeasonGroup().deserialize(response, response.included)),
            );
    }

    /**
     * Add or update the seasonGroup
     */
    public save(seasonGroup: IdentitySeasonGroup, includes: string[] = null): Observable<IdentitySeasonGroup> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(seasonGroup.season.id, seasonGroup.id, includes);

        if (seasonGroup.id) {
            // Update existing
            response = this.http.patch(apiRequestUrl, seasonGroup.serialize());
        } else {
            // Add a new
            response = this.http.post(apiRequestUrl, seasonGroup.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentitySeasonGroup())
                    .deserialize(response, response.included)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified seasonGroup.
     */
    public delete(seasonId: string, seasonGroupId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(seasonId, seasonGroupId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Add one or more teams to the given identity seasonGroup
     *
     * @param seasonId The identity season id
     * @param seasonGroupId The identity seasonGroup id
     * @param identityTeamIds The list of teams to link to the given seasonGroup
     * @returns
     */
    public addTeams(
        seasonId: string,
        seasonGroupId: string,
        identityTeamIds: string[],
    ): Observable<boolean> {
        if (!identityTeamIds || !identityTeamIds.length) {
            return of(true);
        }

        let apiRequestUrl: string = this.getApiUrl(seasonId, seasonGroupId, null, null);
        apiRequestUrl += '/relationships/teams';

        const json: any = {};
        json.data = [];

        identityTeamIds.forEach((identityTeamId: string) => {
            json.data.push({ type: 'team', id: identityTeamId });
        });

        return this.http.post(apiRequestUrl, JSON.stringify(json), true, false)
            .pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Delete one or more teams linked the given identity seasonGroup
     *
     * @param seasonId The identity season id
     * @param seasonGroupId The identity seasonGroup id
     * @param identityTeamIds The list of teams to link to the given seasonGroup
     * @returns
     */
    public deleteTeams(seasonId: string, seasonGroupId: string, identityTeamIds: string[]): Observable<boolean> {
        if (!identityTeamIds || !identityTeamIds.length) {
            return of(true);
        }

        let apiRequestUrl: string = this.getApiUrl(seasonId, seasonGroupId, null, null);
        apiRequestUrl += '/relationships/teams';

        const json: any = { data: [] };
        identityTeamIds.forEach((identityTeamId: string) => {
            json.data.push({ type: 'team', id: identityTeamId });
        });

        return this.http.delete(apiRequestUrl, true, false, this.http.getHeadersForJson(), JSON.stringify(json))
            .pipe(map(() => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(seasonId: string, seasonGroupId: string, includes: string[] = null, queryParams: string[] = null)
    : string {
        let url: string = `${this.identityApiBaseUrl}/seasons/${seasonId}/groups/`;

        if (seasonGroupId) {
            url += seasonGroupId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.cachedSeasonGroupsMap = {};
    }
}
